import React, { useState, useMemo, useEffect } from 'react';
import { Plus, AlertTriangle, ChevronLeft, ChevronRight, CheckSquare, Square, ChevronDown } from 'lucide-react';
import { Card, Btn, Modal, Field, Input } from '../components/UI';
import { sendPush } from '../utils/push';
import { markMockupChange } from '../utils/mockupChanges';
import { taskSetStatus, taskStart, taskComplete, taskSetSubtask, taskSetSubtasks } from '../utils/db';
import { usePresence, PresenceBar } from '../components/Presence';
import { celebrate, pulseTask } from '../utils/celebrate';
import { postNumber } from './content/contentShared';

const CATEGORIES  = ['Reel','Story','AD','Brochure','Voucher','Mockup','Post','Carousel','Calendar','Upwork','N/A'];
// Workflow: Not Started → In Progress → (assignee marks done) Ready for Review
//           → (internal reviewer approves) Client Review
//           → (client approves) Reviewed → Archive
//           → (internal OR client sends back, with a reason) Changes Required → back to board
const STATUSES    = ['Not Started','In Progress','Changes Required','Ready for Review','Client Review','Reviewed'];
const STATUS_COLORS = {
  'Not Started':      { bg:'var(--bg4)',                color:'var(--text3)'  },
  'In Progress':      { bg:'#E7EAEC',                   color:'#0A0A0C'       }, // cool light — active work
  'Changes Required': { bg:'rgba(192,128,121,0.12)',    color:'#C08079'       },
  'Ready for Review': { bg:'rgba(199,154,91,0.12)',     color:'#C79A5B'       },
  'Client Review':    { bg:'rgba(169,137,83,0.12)',     color:'var(--accent)' },
  'Reviewed':         { bg:'rgba(143,176,138,0.12)',    color:'#8FB08A'       },
  'Completed':        { bg:'rgba(143,176,138,0.12)',    color:'#8FB08A'       }, // legacy alias
};
const STATUS_LABELS = {
  'Not Started':'Not Started','In Progress':'In Progress','Changes Required':'Changes Required',
  'Ready for Review':'Completed — Ready for Review','Client Review':'Awaiting Client Approval',
  'Reviewed':'Client Approved — Archived','Completed':'Client Approved — Archived',
};
const statusLabel = s => STATUS_LABELS[s] || s;
const ARCHIVED = s => s==='Reviewed' || s==='Completed';   // client-approved → Archive
const DESIGNER_EDITOR_ROLES = ['Graphic Designer','Video Editor'];
const TASK_EDITOR_ROLES  = ['Content Manager','Scheduler'];
const ADMIN_VIEWER_ROLES = ['Content Manager','Scheduler'];

function today() { return new Date().toISOString().slice(0,10); }
function fmtDate(d) { if(!d) return '—'; return new Date(d+'T00:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); }
function isPast(d) { return d && d < today(); }
function isToday(d) { return d === today(); }
function uniqueId() { return Date.now().toString(36)+Math.random().toString(36).slice(2,6); }

function getEmpRoles(emp) {
  if (!emp) return [];
  return Array.isArray(emp.roles)&&emp.roles.length>0 ? emp.roles : [emp.role].filter(Boolean);
}
function canEdit(session, employees) {
  if (!session) return false;
  if (session.role==='admin') return true;
  const emp = employees.find(e=>e.id===session.empId);
  return getEmpRoles(emp).some(r=>TASK_EDITOR_ROLES.includes(r));
}
function isAdminViewer(session, employees) {
  if (!session) return false;
  if (session.role==='admin') return true;
  const emp = employees.find(e=>e.id===session.empId);
  return getEmpRoles(emp).some(r=>ADMIN_VIEWER_ROLES.includes(r));
}
function isDesignerOrEditor(emp) {
  return getEmpRoles(emp).some(r=>DESIGNER_EDITOR_ROLES.includes(r));
}
function roleColour(emp) {
  // STR outline pills — hairline border + tinted text, transparent fill.
  const roles = getEmpRoles(emp);
  if (roles.includes('Video Editor'))    return { bg:'transparent', color:'#9B8FB0', border:'rgba(155,143,176,0.4)' };
  if (roles.includes('Graphic Designer'))return { bg:'transparent', color:'#C08079', border:'rgba(192,128,121,0.4)' };
  if (roles.includes('Content Manager')) return { bg:'transparent', color:'#8FB08A', border:'rgba(143,176,138,0.4)' };
  if (roles.includes('Scheduler'))       return { bg:'transparent', color:'#8FA3B0', border:'rgba(143,163,176,0.4)' };
  return { bg:'transparent', color:'var(--text3)', border:'var(--border2)' };
}

// What status does this viewer see for a task?
// What status does this viewer see for a task?
//
// Admin / Content Managers / Schedulers (Wajiha, Isha, Shehryar):
//   → See "In Progress" as soon as ANYONE clicks Work on it
//   → See "Completed" when overall status is set to Completed
//   → See Changes Required / Completed from the shared status dropdown
//
// Assigned graphic designers / video editors (Hamza, Numan, Affan, Shoaib):
//   → See "Not Started" until THEY personally click Work on it
//   → See "In Progress" only after THEY clicked Work on it
//   → See "Completed" only after THEY clicked Mark done
//   → See "Changes Required" / "Completed" if editor/admin sets those
//   → Unaffected by what other assignees have done
//
// Non-assigned employees:
//   → See the shared overall status
function viewerStatus(task, session, employees) {
  let overall = task.status || 'Not Started';
  if (overall === 'Completed') overall = 'Reviewed';   // normalise legacy
  const workingOn   = Array.isArray(task.workingOn)   ? task.workingOn   : [];
  const completedBy = Array.isArray(task.completedBy) ? task.completedBy : [];
  const myId        = session?.empId;
  const assignedIds = task.assignedToIds || (task.assignedToId ? [task.assignedToId] : []);

  // Review/client/closed states are global — everyone sees the same thing
  if (overall === 'Ready for Review' || overall === 'Client Review' || overall === 'Reviewed') return overall;

  // Admin, Content Managers, Schedulers — see aggregate team status
  if (isAdminViewer(session, employees)) {
    if ((overall === 'Not Started') && (workingOn.length > 0 || completedBy.length > 0)) return 'In Progress';
    return overall;
  }

  // Assigned employee — strictly personal status, independent of other assignees
  if (myId && assignedIds.includes(myId)) {
    if (completedBy.some(c => c.empId === myId)) return 'Ready for Review'; // their part submitted
    if (workingOn.some(w => w.empId === myId))   return 'In Progress';
    if (overall === 'Changes Required') return 'Changes Required';
    return 'Not Started'; // default — not started until THEY personally click it
  }

  // Non-assigned — see the shared overall status
  return overall;
}

// ── Linkify — render text with any http(s) URLs as clickable links ──
function Linkify({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/gi);
  return parts.map((part, i) =>
    /^https?:\/\//i.test(part)
      ? <a key={i} href={part} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}
           style={{color:'var(--accent)',fontFamily:'var(--mono)',fontSize:'0.9em',fontWeight:400,letterSpacing:'0.01em',wordBreak:'break-all'}}>{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// ── NotesToggle — click the ▴ button to close, not the whole div ──
function NotesToggle({ notes }) {
  const [open, setOpen] = React.useState(false);
  if (!notes) return null;
  return (
    <div style={{marginTop:3}}>
      {!open
        ? <div onClick={e=>{e.stopPropagation();setOpen(true);}} title="Click to expand"
            style={{fontSize:11,color:'var(--text3)',fontStyle:'italic',cursor:'pointer',
                    display:'flex',alignItems:'center',gap:4,overflow:'hidden'}}>
            <span style={{flexShrink:0}}>📝</span>
            <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:280}}>{notes}</span>
            <span style={{flexShrink:0,opacity:0.6}}>▾</span>
          </div>
        : <div style={{fontSize:11,color:'var(--text3)',fontStyle:'italic',
                       background:'var(--bg4)',borderRadius:'var(--radius)',padding:'5px 8px',marginTop:2,
                       borderLeft:'2px solid var(--border2)',wordBreak:'break-word',
                       whiteSpace:'pre-wrap',userSelect:'text',cursor:'text',
                       position:'relative'}}>
            {/* Collapse button — only this collapses, not the text */}
            <button
              onClick={e=>{e.stopPropagation();setOpen(false);}}
              style={{position:'absolute',top:4,right:6,background:'none',border:'none',
                      cursor:'pointer',color:'var(--text3)',fontSize:13,lineHeight:1,
                      padding:'2px 4px',borderRadius:4}}
              title="Collapse">▴</button>
            <span style={{paddingRight:20}}><Linkify text={notes}/></span>
          </div>
      }
    </div>
  );
}

// ── SubTaskList ────────────────────────────────────────────────────
function SubTaskList({ subTasks, taskId, onToggle }) {
  const [open, setOpen] = React.useState(true);
  if (!subTasks||subTasks.length===0) return null;
  const done=subTasks.filter(s=>s.done).length, total=subTasks.length;
  return (
    <div style={{marginTop:6}}>
      <div onClick={e=>{e.stopPropagation();setOpen(o=>!o);}}
        style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:11,color:'var(--text3)',userSelect:'none'}}>
        <ChevronDown size={11} style={{transform:open?'none':'rotate(-90deg)',transition:'transform 0.15s'}}/>
        <span>Subtasks</span>
        <span style={{background:'var(--bg4)',borderRadius:'var(--radius)',padding:'1px 7px',fontSize:10,fontWeight:600,fontFamily:'var(--mono)',
          color:done===total&&total>0?'#8FB08A':'var(--text3)'}}>{done}/{total}</span>
      </div>
      {open&&(
        <div className="expand-anim" style={{marginTop:5,paddingLeft:8,borderLeft:'2px solid var(--border2)',
                     display:'flex',flexDirection:'column',gap:3}}>
          {subTasks.map(st=>(
            <div key={st.id} onClick={e=>{e.stopPropagation();onToggle(taskId,st.id);}}
              style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',padding:'2px 0'}}>
              {st.done?<CheckSquare size={13} color="#8FB08A"/>:<Square size={13} color="var(--text3)"/>}
              <span style={{fontSize:12,color:st.done?'var(--text3)':'var(--text)',
                textDecoration:st.done?'line-through':'none'}}>{st.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Defensive: a person entry should be an object {empId,name,at}, but an old merge bug
// could store it as a JSON string. Parse those back so names always resolve.
function normPeople(arr){
  return (Array.isArray(arr)?arr:[]).map(e=>{
    if (e && typeof e==='object') return e;
    if (typeof e==='string'){ try { return JSON.parse(e); } catch(_) { return null; } }
    return null;
  }).filter(Boolean);
}

// ── NamePill ────────────────────────────────────────────────────────
function NamePill({ w, employees }) {
  if (!w) return null;
  const emp = employees.find(e=>e.id===w.empId);
  const rc  = roleColour(emp);
  return (
    <span style={{fontSize:11,padding:'2px 9px',borderRadius:'var(--radius)',background:rc.bg,
                  color:rc.color,fontWeight:600,border:`1px solid ${rc.border}`}}>
      {w.name||emp?.name||'Unknown'}
    </span>
  );
}

// ── Avatar — STR hairline square with serif initials ────────────────
function Avatar({ name }) {
  const initials = (name||'?').split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  return (
    <div title={name} style={{width:27,height:27,borderRadius:'var(--radius)',background:'transparent',border:'1px solid var(--border2)',
      display:'flex',alignItems:'center',justifyContent:'center',fontSize:11.5,fontWeight:400,color:'var(--text2)',
      fontFamily:'var(--serif)',marginLeft:5,flexShrink:0}}>{initials}</div>
  );
}

// ── Mockup change list ─────────────────────────────────────────────
// For a "Mockup changes" container task, the items are read LIVE from the linked
// mockup (the atomic source of truth) — not from task.subTasks — so they can never
// be lost by a whole-store save. Each item: designer Submits, manager Approves.
function MockupChangeList({ mock, changes, session, employees, editable, createdBy, assignedIds, chBusy,
                            onSubmit, onApprove, onSendBack, onStart }) {
  const [open, setOpen] = React.useState(true);
  const [linkDraft, setLinkDraft] = React.useState({});
  const myId = session?.empId;
  const iAmReviewer = editable || (myId && createdBy === myId);
  const total = changes.length;
  const doneN = changes.filter(c => c.done).length;
  const nameOf = (id) => id === 'admin' ? 'Admin' : (employees.find(e => e.id === id)?.name || '');
  return (
    <div style={{marginTop:6}}>
      <div onClick={e=>{e.stopPropagation();setOpen(o=>!o);}}
        style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:11,color:'var(--text3)',userSelect:'none'}}>
        <ChevronDown size={11} style={{transform:open?'none':'rotate(-90deg)',transition:'transform 0.15s'}}/>
        <span>Client changes</span>
        <span style={{background:'var(--bg4)',borderRadius:'var(--radius)',padding:'1px 7px',fontSize:10,fontWeight:600,fontFamily:'var(--mono)',
          color:doneN===total&&total>0?'#8FB08A':'var(--text3)'}}>{doneN}/{total}</span>
        <div style={{flex:1,maxWidth:80,height:3,borderRadius:2,background:'var(--bg4)',overflow:'hidden'}}>
          <div className="bar-grow" style={{height:'100%',borderRadius:2,background:'#8FB08A',width:`${total>0?(doneN/total)*100:0}%`,transition:'width 0.3s'}}/>
        </div>
      </div>
      {open && (
        <div className="expand-anim" style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
          {changes.map(c => {
            const cbusy = !!chBusy[c.id];
            const status = c.done ? 'done' : c.submitted ? 'submitted' : 'todo';
            const amAssigned = !!myId && (c.assignedTo ? c.assignedTo === myId : (assignedIds || []).includes(myId));
            const pill = status==='done'
              ? { label:'Done', bg:'rgba(143,176,138,0.14)', color:'#8FB08A' }
              : status==='submitted'
                ? { label:'Awaiting approval', bg:'rgba(199,154,91,0.12)', color:'#C79A5B' }
                : c.started
                  ? { label:'In progress', bg:'#E7EAEC', color:'#0A0A0C' }
                  : { label:'With designer', bg:'rgba(143,163,176,0.12)', color:'#8FA3B0' };
            return (
              <div key={c.id} style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'8px 10px',background:'var(--bg3)'}}>
                <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                  <span style={{fontSize:11,fontWeight:800,color:'var(--text2)',flexShrink:0,marginTop:1}}>#{postNumber(c.postIndex, c.postTotal || 0)}</span>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontSize:12.5,color:'var(--text)',lineHeight:1.4,wordBreak:'break-word'}}>{c.note || 'Change requested'}</div>
                    {c.details && <div style={{fontSize:11.5,color:'var(--text3)',marginTop:2,wordBreak:'break-word'}}>↳ {c.details}</div>}
                    <div style={{fontSize:10.5,color:'var(--text3)',marginTop:3}}>{c.assignedTo?`Designer: ${nameOf(c.assignedTo)}`:''}{c.done&&c.doneBy?` · approved by ${nameOf(c.doneBy)}`:''}</div>
                  </div>
                  <span style={{flexShrink:0,background:pill.bg,color:pill.color,fontSize:9,fontWeight:400,fontFamily:'var(--mono)',letterSpacing:'0.1em',textTransform:'uppercase',padding:'4px 8px',borderRadius:'var(--radius)',whiteSpace:'nowrap'}}>{pill.label}</span>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6,marginTop:8}}>
                  {c.finalLink && (status==='submitted'||status==='done') &&
                    <a href={c.finalLink} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{fontSize:11.5,color:'var(--accent2)',fontWeight:600,wordBreak:'break-all'}}>View submission ↗</a>}
                  {status==='todo' && amAssigned && (
                    <>
                      <input value={linkDraft[c.id] ?? (c.finalLink||'')}
                        onChange={e=>{const v=e.target.value;setLinkDraft(d=>({...d,[c.id]:v}));}}
                        onClick={e=>e.stopPropagation()}
                        placeholder="Submission link for this post (Drive/Canva) — optional"
                        style={{width:'100%',padding:'7px 10px',fontSize:12,fontFamily:'var(--mono)',background:'var(--bg4)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',outline:'none',boxSizing:'border-box'}}/>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                        {!c.started
                          ? <Btn size="sm" variant="default" disabled={cbusy} onClick={e=>{e.stopPropagation();onStart(mock,c);}}>{cbusy?'…':'Working on it'}</Btn>
                          : <span style={{fontSize:9.5,fontWeight:400,fontFamily:'var(--mono)',letterSpacing:'0.1em',textTransform:'uppercase',background:'#E7EAEC',color:'#0A0A0C',padding:'4px 9px',borderRadius:'var(--radius)'}}>In progress</span>}
                        <Btn size="sm" variant="success" disabled={cbusy} onClick={e=>{e.stopPropagation();onSubmit(mock,c,linkDraft[c.id] ?? (c.finalLink||''));}}>{cbusy?'…':'Submit for review'}</Btn>
                      </div>
                    </>
                  )}
                  {status==='submitted' && iAmReviewer && (
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <Btn size="sm" variant="success" disabled={cbusy} onClick={e=>{e.stopPropagation();onApprove(mock,c);}}>{cbusy?'…':'✓ Approve'}</Btn>
                      <Btn size="sm" variant="default" disabled={cbusy} onClick={e=>{e.stopPropagation();onSendBack(mock,c);}}>Send back</Btn>
                    </div>
                  )}
                  {status==='submitted' && !iAmReviewer && amAssigned &&
                    <span style={{fontSize:11,color:'var(--text3)'}}>Waiting for the manager to approve.</span>}
                  {status==='done' && <span style={{fontSize:11,color:'var(--accent)',fontWeight:600}}>Client asked to re-check on the mockup.</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Task card (Stitch board style) ─────────────────────────────────
function TaskCard({ task, employees, session, editable, isOnLeave,
                    markStarted, onMarkDone, updateStatus, onReject,
                    toggleSubTask, openEdit, deleteTask,
                    mockByTaskId, mockById, calByTaskId, chBusy2, submitChange, approveChange, sendBackChange, startChange }) {
  const assignedEmps = (task.assignedToIds||(task.assignedToId?[task.assignedToId]:[])).map(id=>employees.find(e=>e.id===id)).filter(Boolean);
  const assignedIds  = task.assignedToIds || (task.assignedToId?[task.assignedToId]:[]);
  const completedBy  = normPeople(task.completedBy);
  // Anyone who has finished belongs under "Done", never still under "Started".
  const workingOn    = normPeople(task.workingOn).filter(w=>!completedBy.some(c=>c.empId===w.empId));
  const archived     = ARCHIVED(task.status);
  const isOverdue    = isPast(task.dueDate)&&!archived&&task.status!=='Ready for Review';
  const isDueToday   = isToday(task.dueDate)&&!archived&&task.status!=='Ready for Review';
  const onLeaveWarn  = assignedEmps.some(e=>isOnLeave(e.id,task.dueDate))&&!archived;
  const myId         = session?.empId;
  const isAssignedToMe = myId && assignedIds.includes(myId);
  const iAmWorking   = myId && workingOn.some(w=>w.empId===myId);
  const iAmCompleted = myId && completedBy.some(c=>c.empId===myId);
  const myStatus     = viewerStatus(task, session, employees);
  const isReadyForReview = task.status==='Ready for Review';
  const isClientReview   = task.status==='Client Review';
  const canReview    = (editable || (myId && task.createdBy===myId)) && !archived;
  const sharedLinks  = completedBy.filter(c=>c.finalLink).map(c=>({name:c.name,link:c.finalLink}));
  if (task.finalLink && sharedLinks.length===0) sharedLinks.push({name:'', link:task.finalLink});

  // distinct role pills among assignees
  const rolePills = [];
  assignedEmps.forEach(e => {
    const roles = getEmpRoles(e);
    const primary = roles[0] || 'Member';
    if (!rolePills.some(p=>p.role===primary)) rolePills.push({ role:primary, rc:roleColour(e) });
  });

  // A "Mockup changes" container task drives its items from the linked mockup.
  const linkedMock = (mockByTaskId && mockByTaskId[task.id]) || (task.mockupId && mockById ? mockById[task.mockupId] : null);
  const mockChanges = linkedMock ? (linkedMock.data?.clientChanges || [])
    .filter(c => c.kind === 'edit' && c.resolved && !c.dismissed)
    .map(c => ({ ...c, postIndex: (linkedMock.data.cells || []).findIndex(x => x.id === c.cellId), postTotal: (linkedMock.data.cells || []).length }))
    .filter(c => c.postIndex >= 0)
    .sort((a, b) => (a.at || '').localeCompare(b.at || '')) : [];
  const isMockTask = !!linkedMock && mockChanges.length > 0;

  // A "Calendar" task reads the real design state of its linked content calendar:
  // how many posts are internally approved, in review, still to design, or waiting on
  // the client. This gives the bar a true picture instead of a status guess.
  const linkedCal = calByTaskId ? calByTaskId[task.id] : null;
  const calStats = (() => {
    if (!linkedCal || !linkedCal.data) return null;
    const grids = Array.isArray(linkedCal.data.grids) ? linkedCal.data.grids : [];
    let posts = grids.flatMap(g => Array.isArray(g.posts) ? g.posts : []);
    if (!posts.length && Array.isArray(linkedCal.data.posts)) posts = linkedCal.data.posts;
    if (!posts.length) return null;
    const awaiting = posts.filter(p => p.awaitingClient).length;
    const active = posts.filter(p => !p.awaitingClient);
    const approved = active.filter(p => p.designStatus === 'approved').length;
    const inReview = active.filter(p => p.designStatus === 'submitted').length;
    const todo = active.length - approved - inReview;
    return { total: active.length, approved, inReview, todo, awaiting };
  })();
  const isCalTask = !!(calStats && calStats.total > 0);

  const subs = Array.isArray(task.subTasks) ? task.subTasks : [];
  const subDone = subs.filter(s=>s.done).length;
  const hasSubs = !isMockTask && subs.length>0;
  // Real progress. A checklist drives it directly. Otherwise it reflects how much of the
  // actual work is done: each assigned person contributes a full share once they've
  // submitted their part (in completedBy) and a half share while they're actively working
  // (in workingOn). Review / completed states read 100; Changes Required shows red.
  const doneStates = myStatus==='Reviewed'||myStatus==='Ready for Review'||myStatus==='Client Review';
  let pct;
  if (myStatus==='Changes Required') pct = 35;               // sent back for rework — never reads "done"
  else if (isCalTask) pct = Math.round(calStats.approved/calStats.total*100);
  else if (hasSubs) pct = Math.round(subDone/subs.length*100);
  else if (doneStates) pct = 100;
  else if (assignedIds.length) {
    const share = assignedIds.reduce((sum,id)=>(
      completedBy.some(c=>c.empId===id) ? sum+1
      : workingOn.some(w=>w.empId===id) ? sum+0.5
      : sum
    ),0);
    pct = Math.round(share/assignedIds.length*100);
    if (pct===0 && myStatus==='In Progress') pct = 10;
  } else {
    pct = myStatus==='In Progress' ? 50 : 0;
  }
  const barColor = myStatus==='Changes Required' ? '#C08079' : pct===100 ? '#8FB08A' : 'var(--accent)';

  // Effective status shown to managers. A task with real progress — subtasks ticked, or
  // calendar posts approved / in review — is never left reading "Not Started". This is
  // display only: it never rewrites task.status and never unlocks the review or client-
  // approval actions, which still key off the real stored status.
  const effStatus = (editable && myStatus==='Not Started'
    && ((hasSubs && subDone>0) || (isCalTask && (calStats.approved>0 || calStats.inReview>0))))
    ? 'In Progress' : myStatus;
  const sc = STATUS_COLORS[effStatus]||STATUS_COLORS['Not Started'];

  // STR whole-card state wash — the card visibly floods to reflect where the work is:
  //   gold  → approved / in-review / client-approved (doneStates)
  //   red   → client/internal asked for changes (Changes Required)
  //   cool  → someone is actively working on it (In Progress)
  // Presentation only; the CSS transitions perform the flood the moment status changes.
  const isGold = doneStates;                       // Ready for Review / Client Review / Reviewed
  const isRed  = myStatus==='Changes Required';
  const isWork = myStatus==='In Progress';
  // Clean, flat cards with clear edges — state shows as a crisp coloured border + a
  // faint bg tint (gold / red), or a cool left-bar while working. No cloudy inner glow.
  const cardBg     = isGold ? '#17150F' : isRed ? '#181210' : 'var(--bg2)';
  const cardBorder = isGold ? 'rgba(169,137,83,0.55)' : isRed ? 'rgba(192,128,121,0.55)' : 'var(--border)';
  const cardGlow   = isWork
    ? 'inset 3px 0 0 rgba(231,234,236,0.55), 0 1px 0 rgba(0,0,0,0.35)'
    : '0 1px 0 rgba(0,0,0,0.35)';

  return (
    <div data-task-id={task.id} className={'fx-card'+(isOverdue?' overdue-pulse':'')} style={{
      background: cardBg, border: '1px solid '+cardBorder, borderRadius:'var(--radius)',
      boxShadow: cardGlow, padding:'18px 20px', display:'flex', flexDirection:'column', gap:12,
      borderLeft: isOverdue?'3px solid #C08079':onLeaveWarn?'3px solid #C79A5B':'1px solid '+cardBorder,
      transition:'background-color .45s ease, border-color .45s ease, box-shadow .45s ease, transform .25s cubic-bezier(.2,.8,.2,1)',
    }}>
      {/* Top: role pills + avatars */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {rolePills.length>0 ? rolePills.map((p,i)=>(
            <span key={i} className="role-pill" style={{background:p.rc.bg,color:p.rc.color,textTransform:'uppercase',fontSize:10,letterSpacing:'0.04em'}}>{p.role}</span>
          )) : <span className="role-pill" style={{background:'var(--bg4)',color:'var(--text3)',textTransform:'uppercase',fontSize:10}}>Unassigned</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',paddingLeft:6}}>
          {assignedEmps.slice(0,4).map(e=><Avatar key={e.id} name={e.name}/>)}
        </div>
      </div>

      {/* Title + meta — mono brass client tag · serif name · mono due line */}
      <div>
        {task.clientName && <div style={{fontFamily:'var(--mono)',fontSize:9.5,fontWeight:400,letterSpacing:'0.14em',textTransform:'uppercase',color:'var(--accent)',marginBottom:6}}>{task.clientName}</div>}
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:18,color:'var(--text)',letterSpacing:'-0.01em'}}>{task.taskName}</span>
          {isOverdue&&<span style={{fontSize:9.5,fontWeight:400,fontFamily:'var(--mono)',letterSpacing:'0.1em',color:'#C08079',display:'flex',alignItems:'center',gap:3}}><AlertTriangle size={11}/>OVERDUE</span>}
          {isDueToday&&!isOverdue&&<span className="glow-soft" style={{fontSize:9.5,fontWeight:400,fontFamily:'var(--mono)',letterSpacing:'0.1em',color:'#C79A5B'}}>DUE TODAY</span>}
        </div>
        <div style={{fontFamily:'var(--mono)',fontSize:10,fontWeight:400,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--text3)',marginTop:6}}>
          {task.category} · Due {fmtDate(task.dueDate)}
        </div>
        {task.notes && <div style={{marginTop:8}}><NotesToggle notes={task.notes}/></div>}
      </div>

      {/* Progress bar (always) + checklist / change-list detail below it */}
      {isMockTask ? (
        <MockupChangeList mock={linkedMock} changes={mockChanges} session={session} employees={employees}
          editable={editable} createdBy={task.createdBy} assignedIds={assignedIds} chBusy={chBusy2||{}}
          onSubmit={submitChange} onApprove={approveChange} onSendBack={sendBackChange} onStart={startChange}/>
      ) : (
        <div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,fontWeight:400,color:'var(--text3)',marginBottom:6,fontFamily:'var(--mono)',letterSpacing:'0.08em'}}>
            <span style={{textTransform:'uppercase'}}>{isCalTask?'Calendar progress':'Progress'}</span>
            <span>{isCalTask ? `${calStats.approved}/${calStats.total} approved · ${pct}%` : hasSubs ? `${subDone}/${subs.length} · ${pct}%` : `${pct}%`}</span>
          </div>
          <div style={{height:6,borderRadius:3,background:'var(--bg4)',overflow:'hidden'}}>
            <div className="bar-grow" style={{height:'100%',borderRadius:3,width:`${pct}%`,background:barColor,transition:'width 0.4s cubic-bezier(.2,.8,.2,1)'}}/>
          </div>
          {isCalTask && (
            <div style={{display:'flex',gap:12,flexWrap:'wrap',marginTop:7,fontSize:11,color:'var(--text3)',fontWeight:500}}>
              {calStats.inReview>0 && <span style={{color:'var(--accent)'}}>{calStats.inReview} in review</span>}
              {calStats.todo>0 && <span>{calStats.todo} to design</span>}
              {calStats.approved>0 && <span style={{color:'#8FB08A'}}>{calStats.approved} approved</span>}
              {calStats.awaiting>0 && <span style={{color:'#C79A5B'}}>{calStats.awaiting} awaiting client</span>}
            </div>
          )}
          {hasSubs && (
            <div style={{marginTop:10}}>
              <SubTaskList subTasks={task.subTasks} taskId={task.id} onToggle={toggleSubTask}/>
            </div>
          )}
        </div>
      )}

      {/* Started / completed name pills */}
      {(workingOn.length>0||completedBy.length>0)&&(
        <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
          {workingOn.length>0&&<div style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
            <span style={{fontSize:9,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.1em'}}>Started</span>
            {workingOn.map(w=><NamePill key={w.empId} w={w} employees={employees}/>)}
          </div>}
          {completedBy.length>0&&<div style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
            <span style={{fontSize:9,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.1em'}}>Done</span>
            {completedBy.map(w=><NamePill key={w.empId} w={w} employees={employees}/>)}
          </div>}
        </div>
      )}

      {/* Final link / where it was shared */}
      {sharedLinks.length>0&&(
        <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'8px 11px'}}>
          <div style={{fontSize:9,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:4}}>Final Link / Where it was shared</div>
          {sharedLinks.map((s,i)=>(
            <div key={i} style={{fontSize:12,color:'var(--text2)',wordBreak:'break-word',marginTop:i>0?3:0}}>
              {s.name&&<span style={{color:'var(--text3)'}}>{s.name}: </span>}
              <Linkify text={s.link}/>
            </div>
          ))}
        </div>
      )}

      {/* Reason for requested changes */}
      {task.status==='Changes Required'&&task.changeReason&&(
        <div style={{background:'rgba(192,128,121,0.08)',border:'1px solid rgba(192,128,121,0.3)',borderRadius:'var(--radius)',padding:'8px 11px'}}>
          <div style={{fontSize:9.5,fontWeight:400,fontFamily:'var(--mono)',color:'#C08079',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:3}}>
            Changes requested{task.changeBy?` · ${task.changeBy==='client'?'Client':'Internal review'}`:''}
          </div>
          <div style={{fontSize:12.5,color:'var(--text2)',whiteSpace:'pre-wrap',wordBreak:'break-word'}}><Linkify text={task.changeReason}/></div>
        </div>
      )}

      {/* Footer: status + action */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,borderTop:'1px solid var(--border)',paddingTop:12,marginTop:2,flexWrap:'wrap'}}>
        {editable
          ? <select value={ARCHIVED(task.status)?'Reviewed':effStatus} onChange={e=>updateStatus(task.id,e.target.value)}
              style={{padding:'6px 12px',fontSize:10,fontWeight:400,border:'none',borderRadius:'var(--radius)',cursor:'pointer',maxWidth:'100%',
                background:sc.bg,color:sc.color,appearance:'none',WebkitAppearance:'none',textTransform:'uppercase',letterSpacing:'0.1em',fontFamily:'var(--mono)'}}>
              {STATUSES.map(s=><option key={s} value={s} style={{background:'var(--bg2)',color:'var(--text)'}}>{statusLabel(s)}</option>)}
            </select>
          : <span style={{padding:'6px 12px',fontSize:10,fontWeight:400,borderRadius:'var(--radius)',background:sc.bg,color:sc.color,
              textTransform:'uppercase',letterSpacing:'0.1em',fontFamily:'var(--mono)'}}>{statusLabel(effStatus)}</span>
        }
        <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
          {/* Internal reviewer actions — sends to Client Review */}
          {isReadyForReview&&canReview&&(
            <>
              <Btn size="sm" variant="success" onClick={()=>updateStatus(task.id,'Client Review')}>✓ Approve → Client</Btn>
              <Btn size="sm" variant="default" onClick={()=>onReject(task.id,'internal')}>Request changes</Btn>
            </>
          )}
          {/* Client review actions — client approves (archive) or rejects (back to board) */}
          {isClientReview&&canReview&&(
            <>
              <Btn size="sm" variant="success" onClick={()=>updateStatus(task.id,'Reviewed')}>✓ Client approved</Btn>
              <Btn size="sm" variant="danger" onClick={()=>onReject(task.id,'client')}>Not approved</Btn>
            </>
          )}
          {/* Assignee work actions (suppressed for mockup-change tasks — those use per-change Submit/Approve) */}
          {!isMockTask&&isAssignedToMe&&!archived&&!isReadyForReview&&!iAmCompleted&&(
            <>
              {!iAmWorking&&<Btn size="sm" variant="default" onClick={()=>markStarted(task.id)}>Work on it</Btn>}
              {iAmWorking&&<Btn size="sm" variant="success" onClick={()=>onMarkDone(task.id)}>Mark Done</Btn>}
            </>
          )}
          {editable&&<>
            <Btn size="sm" variant="default" onClick={()=>openEdit(task)}>Edit</Btn>
            <Btn size="sm" variant="danger" onClick={()=>deleteTask(task.id)}>×</Btn>
          </>}
        </div>
      </div>
    </div>
  );
}

// ── Client group — collapsible board section ───────────────────────
// Collapsed by default (owner's call): with every client open the board ran very
// long. Click a client to open it; it stays open across live refreshes (keyed by name).
function ClientGroup({ clientName, tasks, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{marginBottom:8}}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{display:'flex',alignItems:'center',gap:10,padding:'6px 2px 14px',cursor:'pointer',userSelect:'none'}}>
        <ChevronDown size={18} style={{transform:open?'none':'rotate(-90deg)',transition:'transform 0.15s',color:'var(--text2)',flexShrink:0}}/>
        <span style={{fontFamily:'var(--serif)',fontWeight:400,fontSize:20,color:'var(--text)',letterSpacing:'-0.01em'}}>{clientName||'No client'}</span>
        <span className="t-label" style={{background:'var(--bg4)',borderRadius:'var(--radius)',padding:'4px 10px'}}>{tasks.length} {tasks.length===1?'task':'tasks'}</span>
        <div style={{flex:1,height:1,background:'var(--border)',marginLeft:4}}/>
      </div>
      {open&&(
        <div className="stagger" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,300px),1fr))',gap:18}}>
          {tasks.map(task=><TaskCard key={task.id} task={task} {...props}/>)}
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────
export default function TasksPage({ store, persist, showToast, session }) {
  const employees = store.employees     || [];
  const leaveReqs = store.leaveRequests || [];
  const editable  = canEdit(session, employees);

  // Active (non-archived) task count per person, so the CM can see who's free
  // before tagging. Read-only, derived from tasks — nothing to maintain.
  const activeLoad = useMemo(() => {
    const m = {};
    (store.tasks || []).forEach(t => {
      if (ARCHIVED(t.status)) return;
      const ids = t.assignedToIds || ((t.assignedToId||t.assigneeId) ? [t.assignedToId||t.assigneeId] : []);
      ids.forEach(id => { m[id] = (m[id] || 0) + 1; });
    });
    return m;
  }, [store.tasks]);

  const [showModal,    setShowModal]    = useState(false);
  const [editingTask,  setEditingTask]  = useState(null);
  const [form,         setForm]         = useState(()=>emptyForm(session,employees));
  const [filterDate,   setFilterDate]   = useState(today());
  const [rangeStart,   setRangeStart]   = useState(()=>{ const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [rangeEnd,     setRangeEnd]     = useState(today());
  const [filterEmp,    setFilterEmp]    = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Live presence — when two people open the same task's editor, they see each other.
  const myId = session?.empId || 'admin';
  const myEmp = employees.find(e => e.id === myId);
  const myName = myId === 'admin' ? 'Admin' : (myEmp && myEmp.name) || 'Someone';
  const { peers, setEditing } = usePresence(showModal && editingTask ? 'task:' + editingTask.id : null, { id: myId, name: myName });
  useEffect(() => { setEditing(showModal && !!editingTask); }, [showModal, editingTask, setEditing]);
  const [viewMode,     setViewMode]     = useState('all');
  const [groupByClient,setGroupByClient]= useState(true);
  const [completingId, setCompletingId] = useState(null);  // task awaiting "final link" before completion
  const [linkText,     setLinkText]     = useState('');
  const [rejecting,    setRejecting]    = useState(null);  // { id, by:'internal'|'client' } awaiting a reason
  const [rejectReason, setRejectReason] = useState('');

  const me = session?.empId ? employees.find(e=>e.id===session.empId) : null;
  // Build a notification object addressed to a recipient (empId, or 'admin')
  function notif(empId, message, task, kind) {
    return { id:uniqueId(), empId, message, taskId:task.id, taskName:task.taskName,
             client:task.clientName||'', kind, createdAt:new Date().toISOString(), read:false };
  }

  function emptyForm(sess, emps) {
    const me = sess?.empId ? emps.find(e=>e.id===sess.empId) : null;
    return { clientName:'', taskName:'', category:'Post',
      assigneeId:sess?.empId||'', assigneeName:me?.name||'',
      assignedToIds:[], dueDate:today(), status:'Not Started', notes:'',
      assignMode:'any', completionRoles:{}, hasSubTasks:false, subTasks:[] };
  }

  const approvedLeaves = useMemo(()=>{
    const map={};
    leaveReqs.filter(r=>r.status==='approved').forEach(r=>{
      if(!map[r.empId]) map[r.empId]=new Set();
      const start=new Date(r.date), end=r.endDate?new Date(r.endDate):start;
      for(let d=new Date(start);d<=end;d.setDate(d.getDate()+1))
        map[r.empId].add(d.toISOString().slice(0,10));
    });
    return map;
  },[leaveReqs]);
  function isOnLeave(empId,dateStr){ return !!(approvedLeaves[empId]?.has(dateStr)); }

  // Always read tasks fresh from store
  const allTasks = store.tasks || [];

  // Link mockups to their "Mockup changes" container task, so those tasks can
  // render their items live from the mockup (the atomic source of truth).
  const mockups = store.mockups || [];
  const mockByTaskId = useMemo(()=>{ const m={}; mockups.forEach(mk=>{ const tid=mk.data?.changesTaskId; if(tid) m[tid]=mk; }); return m; },[mockups]);
  const mockById     = useMemo(()=>{ const m={}; mockups.forEach(mk=>{ m[mk.id]=mk; }); return m; },[mockups]);
  // Link content calendars to the task the app created when they were assigned, so a
  // "Calendar" task's progress can read the real per-post design state inside the calendar.
  const calByTaskId  = useMemo(()=>{ const m={}; (store.contentCalendars||[]).forEach(c=>{ const tid=c.data?.taskId; if(tid) m[tid]=c; }); return m; },[store.contentCalendars]);
  const [chBusy2, setChBusy2] = useState({});

  async function runChg(change, fn, okMsg){
    setChBusy2(b=>({...b,[change.id]:true}));
    try { const res = await fn(); if(okMsg) showToast(typeof okMsg==='function'?okMsg(res):okMsg); return res; }
    catch(e){ showToast('Could not save — '+(e.message||'please try again'),'error'); }
    finally { setChBusy2(b=>{ const x={...b}; delete x[change.id]; return x; }); }
  }
  function startChange(mock, change){
    const empId = session?.empId || 'admin';
    runChg(change, ()=>markMockupChange(mock.id, change.id, 'start', { empId }), 'Marked as in progress.');
  }
  function submitChange(mock, change, link){
    const empId = session?.empId || 'admin';
    const extra = { empId };
    if ((link||'').trim()) extra.link = link.trim();
    celebrate('submit');
    runChg(change, ()=>markMockupChange(mock.id, change.id, 'submit', extra), 'Submitted — the manager will review it.')
      .then(()=>{ try{ sendPush(mock.createdBy, 'Mockup change submitted', `${mock.clientName} · a change is ready to review`, '/'); }catch(e){} });
  }
  function approveChange(mock, change){
    const empId = session?.empId || 'admin';
    celebrate('approve');
    runChg(change, ()=>markMockupChange(mock.id, change.id, 'done', { empId }),
      (res)=>{
        if(!res) return 'Approved.';
        if(!res.allDone) return 'Approved — this post still has other changes open, so it stays as action required.';
        return res.coverChanged===false
          ? 'Approved — but the post image wasn’t changed, so the client still sees it as action required.'
          : 'Approved — the client will be asked to re-check this post.';
      })
      .then(()=>{ if(change.assignedTo){ try{ sendPush(change.assignedTo, 'Change approved', `${mock.clientName} · your change was approved`, '/'); }catch(e){} } });
  }
  function sendBackChange(mock, change){
    runChg(change, ()=>markMockupChange(mock.id, change.id, 'reopen', {}), 'Sent back to the designer.')
      .then(()=>{ if(change.assignedTo){ try{ sendPush(change.assignedTo, 'Changes needed again', `${mock.clientName} · please revisit this change`, '/'); }catch(e){} } });
  }

  const visibleTasks = useMemo(()=>{
    let list;
    if (viewMode==='archive') {
      list = allTasks.filter(t=>ARCHIVED(t.status));
    } else if (viewMode==='clientreview') {
      list = allTasks.filter(t=>t.status==='Client Review');
    } else {
      // active board excludes both the client-review queue and the archive
      list = allTasks.filter(t=>!ARCHIVED(t.status) && t.status!=='Client Review');
      if (viewMode==='day')   list=list.filter(t=>t.dueDate<=filterDate);
      if (viewMode==='range') list=list.filter(t=>{ const inR=d=>d&&d>=rangeStart&&d<=rangeEnd; return inR(t.dueDate); });
    }
    if (filterEmp!=='all') list=list.filter(t=>{
      const ids=t.assignedToIds||(t.assignedToId?[t.assignedToId]:[]);
      return ids.includes(filterEmp);
    });
    if (filterStatus!=='all') list=list.filter(t=>t.status===filterStatus);
    return [...list].sort((a,b)=>{
      if (viewMode==='archive') return (b.completedDate||b.dueDate||'').localeCompare(a.completedDate||a.dueDate||'');
      return (a.dueDate||'').localeCompare(b.dueDate||'');
    });
  },[allTasks,viewMode,filterDate,rangeStart,rangeEnd,filterEmp,filterStatus]);

  const overdueCount = useMemo(()=>allTasks.filter(t=>isPast(t.dueDate)&&!ARCHIVED(t.status)&&t.status!=='Ready for Review').length,[allTasks]);

  const dayLabel = useMemo(()=>{
    if(filterDate===today()) return 'Today';
    return new Date(filterDate+'T12:00:00').toLocaleDateString('en-GB',{weekday:'short',day:'2-digit',month:'short'});
  },[filterDate]);

  const fmtRangeDay = ds => new Date(ds+'T12:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short'});
  const rangeLabel = useMemo(()=>`${fmtRangeDay(rangeStart)} – ${fmtRangeDay(rangeEnd)}`,[rangeStart,rangeEnd]);

  function shiftDay(n) {
    const [y,m,d]=filterDate.split('-').map(Number);
    const dt=new Date(y,m-1,d+n);
    setFilterDate(`${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`);
  }

  function openNew(){ setEditingTask(null); setForm(emptyForm(session,employees)); setShowModal(true); }
  function openEdit(task){
    setEditingTask(task);
    setForm({ clientName:task.clientName||'', taskName:task.taskName||'',
      category:task.category||'Post', assigneeId:task.assigneeId||'',
      assigneeName:task.assigneeName||'',
      assignedToIds:task.assignedToIds||(task.assignedToId?[task.assignedToId]:[]),
      dueDate:task.dueDate||today(), status:task.status||'Not Started',
      notes:task.notes||'', assignMode:task.assignMode||'all', completionRoles:task.completionRoles||{}, hasSubTasks:!!(task.subTasks&&task.subTasks.length>0),
      subTasks:task.subTasks||[] });
    setShowModal(true);
  }

  function saveTask(){
    if(!form.taskName.trim())       return showToast('Task name is required.','error');
    if(!form.assignedToIds.length)  return showToast('Assign to at least one person.','error');
    const conflict=form.assignedToIds.find(id=>isOnLeave(id,form.dueDate));
    if(conflict){
      const n=employees.find(e=>e.id===conflict)?.name||'Someone';
      return showToast(`${n} is on approved leave on ${fmtDate(form.dueDate)}.`,'error');
    }
    const cleanSubs = form.hasSubTasks ? form.subTasks.filter(s=>s.title.trim()) : [];
    const now = new Date().toISOString();
    const freshTasks = store.tasks || []; // always read fresh
    const prevIds = editingTask ? (editingTask.assignedToIds||(editingTask.assignedToId?[editingTask.assignedToId]:[])) : [];
    const task = editingTask
      ? {...editingTask,...form,subTasks:cleanSubs,
          completedDate:ARCHIVED(form.status)?(editingTask.completedDate||today()):undefined}
      : {id:uniqueId(),...form,subTasks:cleanSubs,workingOn:[],completedBy:[],
          createdAt:now,createdBy:session?.empId||'admin'};
    const newTasks = editingTask
      ? freshTasks.map(t=>t.id===task.id?task:t)
      : [...freshTasks, task];
    // Notify anyone newly assigned to this task
    const newlyAssigned = (form.assignedToIds||[]).filter(id => id!==session?.empId && !prevIds.includes(id));
    const assignerName = me?.name || 'Your manager';
    const notifs = [...(store.notifications||[]),
      ...newlyAssigned.map(id => notif(id, `${assignerName} assigned you a task: "${task.taskName}"${task.clientName?` for ${task.clientName}`:''} · due ${fmtDate(task.dueDate)}`, task, 'assigned'))];
    newlyAssigned.forEach(id => sendPush(id, 'New task assigned', task.taskName + (task.clientName?` · ${task.clientName}`:''), '/'));
    persist({...store, tasks:newTasks, notifications:notifs});
    // Editing an existing task: status/completedDate are written atomically (task_merge
    // skips them). A brand-new task carries its initial status through the INSERT.
    if(editingTask && form.status!==editingTask.status) taskSetStatus(task.id, form.status).catch(()=>{});
    // subTasks are skip-listed by task_merge, so structural list edits (add / remove /
    // rename) on an existing task must go through the atomic list writer or they're lost.
    if(editingTask) taskSetSubtasks(task.id, cleanSubs).catch(()=>{});
    showToast(editingTask?'Task updated.':'Task created.');
    setShowModal(false);
  }

  // The moment a task moves, mark it: sparks for a submission, confetti for an approval,
  // a shake when it's sent back. (utils/celebrate.js — off for "reduce motion".)
  function celebrateStatus(taskId, status){
    if(status==='Ready for Review'||status==='Client Review'){ celebrate('submit'); pulseTask(taskId,'flash'); }
    else if(status==='Reviewed'||status==='Completed'){ celebrate('approve'); pulseTask(taskId,'pop'); }
    else if(status==='Changes Required'){ pulseTask(taskId,'shake'); }
  }
  function updateStatus(taskId,newStatus,reason='',changeBy=''){
    celebrateStatus(taskId,newStatus);
    const freshTasks = store.tasks||[];
    const task = freshTasks.find(t=>t.id===taskId);
    const assignedIds = task ? (task.assignedToIds||(task.assignedToId?[task.assignedToId]:[])) : [];
    const reviewerId  = task ? (task.createdBy||'admin') : 'admin';
    const updated=freshTasks.map(t=>{
      if(t.id!==taskId) return t;
      const patch={...t,status:newStatus};
      if(newStatus==='Reviewed') patch.completedDate=today();
      if(newStatus==='Not Started'){ patch.workingOn=[]; patch.completedBy=[]; }
      if(newStatus==='Changes Required'){ patch.completedBy=[]; patch.changeReason=reason||t.changeReason||''; patch.changeBy=changeBy||t.changeBy||''; }
      else { patch.changeReason=''; patch.changeBy=''; }   // clear the reason once it moves forward again
      return patch;
    });
    let notifs=[...(store.notifications||[])];
    if(task){
      if(newStatus==='Ready for Review' && reviewerId!==session?.empId){
        notifs.push(notif(reviewerId, `"${task.taskName}" is completed and ready for your review`, task, 'review'));
        sendPush(reviewerId, 'Ready for your review', task.taskName, '/');
      }
      if(newStatus==='Client Review' && reviewerId!==session?.empId){
        notifs.push(notif(reviewerId, `"${task.taskName}" passed internal review — awaiting client approval`, task, 'review'));
        sendPush(reviewerId, 'Awaiting client approval', task.taskName, '/');
      }
      if(newStatus==='Reviewed')
        assignedIds.forEach(id=>{ notifs.push(notif(id, `Your task "${task.taskName}" was approved by the client ✅`, task, 'reviewed')); sendPush(id, 'Task approved ✅', task.taskName, '/'); });
      if(newStatus==='Changes Required'){
        const who = changeBy==='client' ? 'the client' : 'internal review';
        assignedIds.forEach(id=>{ notifs.push(notif(id, `Changes requested on "${task.taskName}" by ${who}${reason?`: ${reason}`:''}`, task, 'changes')); sendPush(id, 'Changes requested', task.taskName + (reason?`: ${reason}`:''), '/'); });
      }
    }
    persist({...store,tasks:updated,notifications:notifs});
    // Status is authoritative via the atomic RPC (task_merge no longer writes it), so a
    // lagging tab's whole-store flush can never revert this approval/rejection.
    taskSetStatus(taskId, newStatus, { reason, changeBy }).catch(()=>showToast('Status didn’t save — check your connection.','error'));
    if(newStatus==='Client Review') showToast('Approved — sent for client review.');
    else if(newStatus==='Reviewed') showToast('Client approved & archived.');
    else if(newStatus==='Changes Required') showToast('Sent back for changes.');
  }

  // Open the reason prompt before sending a task back for changes
  function onReject(taskId, by){ setRejecting({ id:taskId, by }); setRejectReason(''); }

  // Called by the "Mark Done" button — designers/editors must supply a final link first
  function onMarkDone(taskId){
    if(isDesignerOrEditor(me)){ setCompletingId(taskId); setLinkText(''); }
    else markMyCompleted(taskId,'');
  }

  function markStarted(taskId){
    const empId=session?.empId; if(!empId) return;
    pulseTask(taskId,'pop');
    const empName=employees.find(e=>e.id===empId)?.name||'Unnamed';
    const freshTasks=store.tasks||[];
    const task=freshTasks.find(t=>t.id===taskId); if(!task) return;
    const workingOn=Array.isArray(task.workingOn)?task.workingOn:[];
    if(workingOn.some(w=>w.empId===empId)) return;
    const completedBy=(Array.isArray(task.completedBy)?task.completedBy:[]).filter(c=>c.empId!==empId);
    const bumped = task.status==='Not Started' ? 'In Progress' : task.status;
    const at=new Date().toISOString();
    const updatedTask={...task,status:bumped,workingOn:[...workingOn,{empId,name:empName,at}],completedBy};
    persist({...store,tasks:freshTasks.map(t=>t.id===taskId?updatedTask:t)});
    // workingOn/completedBy are written ATOMICALLY (task_merge no longer touches them),
    // so a stale flush can never revert who started.
    taskStart(taskId, empId, empName, at).catch(()=>showToast('Couldn’t save — check your connection.','error'));
    if(bumped!==task.status) taskSetStatus(taskId, bumped).catch(()=>{});
    showToast('Marked as started.');
  }

  function markMyCompleted(taskId, finalLink=''){
    const empId=session?.empId; if(!empId) return;
    celebrate('submit'); pulseTask(taskId,'flash');   // "done from me" 
    const empName=employees.find(e=>e.id===empId)?.name||'Unnamed';
    const freshTasks=store.tasks||[];
    const task=freshTasks.find(t=>t.id===taskId); if(!task) return;
    const completedBy=Array.isArray(task.completedBy)?task.completedBy:[];
    if(completedBy.some(c=>c.empId===empId)) return;
    const workingOn=(Array.isArray(task.workingOn)?task.workingOn:[]).filter(w=>w.empId!==empId);
    const assignedIds=task.assignedToIds||(task.assignedToId?[task.assignedToId]:[]);
    const at=new Date().toISOString();
    const newCompletedBy=[...completedBy,{empId,name:empName,at,finalLink:finalLink||''}];
    // 'all' = every assignee must finish (shared work e.g. calendars); otherwise
    // the FIRST person to finish completes the whole task (whoever's-free tasks).
    // Legacy tasks with no assignMode keep the old 'everyone' behaviour.
    const mode = task.assignMode || 'all';
    const completedInRole = (role)=>newCompletedBy.filter(c=>{const e=employees.find(x=>x.id===c.empId);return e&&getEmpRoles(e).includes(role);}).length;
    const allDone = mode === 'all'
      ? assignedIds.every(id=>newCompletedBy.some(c=>c.empId===id))
      : mode === 'roles'
        ? (()=>{ const reqs=Object.entries(task.completionRoles||{}).filter(([,n])=>n>0);
                 return reqs.length ? reqs.every(([role,n])=>completedInRole(role)>=n) : newCompletedBy.length>=1; })()
        : newCompletedBy.length >= 1;
    // Rule satisfied → up for review. Otherwise partial work still advances it off "Not Started".
    const newStatus = allDone ? 'Ready for Review'
      : (task.status==='Not Started' ? 'In Progress' : task.status);
    const updatedTask={...task,workingOn,completedBy:newCompletedBy,
      status:newStatus,
      finalLink: finalLink || task.finalLink || '',
      completedDate:task.completedDate};
    let notifs=[...(store.notifications||[])];
    if(allDone){
      const reviewerId=task.createdBy||'admin';
      if(reviewerId!==empId){
        notifs.push(notif(reviewerId, `"${task.taskName}" is completed and ready for your review`, task, 'review'));
        sendPush(reviewerId, 'Ready for your review', task.taskName, '/');
      }
    }
    persist({...store,tasks:freshTasks.map(t=>t.id===taskId?updatedTask:t),notifications:notifs});
    // The submission (completedBy + finalLink) is written ATOMICALLY so a lagging tab
    // can never wipe it — this is the fix for submissions reverting.
    taskComplete(taskId, empId, empName, at, finalLink||task.finalLink||'').catch(()=>showToast('Submission didn’t save — check your connection.','error'));
    if(newStatus!==task.status) taskSetStatus(taskId, newStatus).catch(()=>{});
    showToast(allDone?'Submitted for review.':'Marked your part as done.');
  }

  function toggleSubTask(taskId,subId){
    const freshTasks=store.tasks||[];
    const t0=freshTasks.find(t=>t.id===taskId);
    const newDone=!((t0?.subTasks||[]).find(s=>s.id===subId)?.done);
    if(newDone) celebrate('small');
    persist({...store,tasks:freshTasks.map(t=>
      t.id!==taskId?t:{...t,subTasks:(t.subTasks||[]).map(s=>s.id===subId?{...s,done:newDone}:s)}
    )});
    // subTasks are written ATOMICALLY so a stale flush can't untick a completed subtask.
    taskSetSubtask(taskId, subId, newDone).catch(()=>showToast('Couldn’t save — check your connection.','error'));
  }

  function deleteTask(id){
    if(!window.confirm('Delete this task?')) return;
    persist({...store,tasks:(store.tasks||[]).filter(t=>t.id!==id)}, {deletedIds:{tasks:[id]}});
    showToast('Task deleted.');
  }

  function addSubTask(){ setForm(p=>({...p,subTasks:[...p.subTasks,{id:uniqueId(),title:'',done:false}]})); }
  function updateSubTask(id,title){ setForm(p=>({...p,subTasks:p.subTasks.map(s=>s.id===id?{...s,title}:s)})); }
  function removeSubTask(id){ setForm(p=>({...p,subTasks:p.subTasks.filter(s=>s.id!==id)})); }

  // Group tasks by client name
  const tasksByClient = useMemo(()=>{
    if (!groupByClient) return null;
    const groups={};
    visibleTasks.forEach(t=>{
      const key=t.clientName||'(No client)';
      if(!groups[key]) groups[key]=[];
      groups[key].push(t);
    });
    return Object.entries(groups).sort(([a],[b])=>a.localeCompare(b));
  },[visibleTasks,groupByClient]);

  const cardProps = { employees, session, editable, isOnLeave,
    markStarted, onMarkDone, updateStatus, onReject, toggleSubTask, openEdit, deleteTask,
    mockByTaskId, mockById, calByTaskId, chBusy2, submitChange, approveChange, sendBackChange, startChange };

  const inp={width:'100%',padding:'8px 12px',fontSize:13,background:'var(--bg3)',
    border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',outline:'none',boxSizing:'border-box'};
  const sel={...inp,cursor:'pointer'};

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <h1 className="t-h1">{viewMode==='archive'?'Task Archive':viewMode==='clientreview'?'Client Review':'Shared Task Board'}</h1>
          <p className="t-sub" style={{marginTop:4}}>
            {allTasks.filter(t=>!ARCHIVED(t.status)&&t.status!=='Client Review').length} active · {allTasks.filter(t=>t.status==='Client Review').length} in client review · {allTasks.filter(t=>ARCHIVED(t.status)).length} archived
          </p>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
          <div className="segmented" style={{padding:3}}>
            <button className={viewMode==='all'?'active':''} onClick={()=>setViewMode('all')} style={{padding:'6px 13px',fontSize:12.5}}>All tasks</button>
            <button className={viewMode==='day'?'active':''} onClick={()=>setViewMode('day')} style={{padding:'6px 13px',fontSize:12.5}}>Day view</button>
            <button className={viewMode==='range'?'active':''} onClick={()=>setViewMode('range')} style={{padding:'6px 13px',fontSize:12.5}}>Date range</button>
            <button className={viewMode==='clientreview'?'active':''} onClick={()=>setViewMode('clientreview')} style={{padding:'6px 13px',fontSize:12.5}}>Client Review</button>
            <button className={viewMode==='archive'?'active':''} onClick={()=>setViewMode('archive')} style={{padding:'6px 13px',fontSize:12.5}}>Archive</button>
          </div>
          <div className="segmented" style={{padding:3}}>
            <button className={groupByClient?'active':''} onClick={()=>setGroupByClient(true)} style={{padding:'6px 13px',fontSize:12.5}}>By client</button>
            <button className={!groupByClient?'active':''} onClick={()=>setGroupByClient(false)} style={{padding:'6px 13px',fontSize:12.5}}>Flat</button>
          </div>
          {editable&&<Btn variant="primary" onClick={openNew}><Plus size={15}/>New task</Btn>}
        </div>
      </div>

      {/* Filters */}
      <Card style={{padding:'14px 20px'}}>
        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          {viewMode==='day'&&<>
            <Btn size="sm" variant="default" onClick={()=>shiftDay(-1)}><ChevronLeft size={14}/></Btn>
            <div style={{fontWeight:600,fontSize:15,color:'var(--text)',minWidth:120,textAlign:'center'}}>{dayLabel}</div>
            <Btn size="sm" variant="default" onClick={()=>shiftDay(1)}><ChevronRight size={14}/></Btn>
            <input type="date" value={filterDate} onChange={e=>setFilterDate(e.target.value)}
              style={{...inp,width:'auto',padding:'7px 10px',fontSize:12,background:'var(--bg3)'}}/>
            {overdueCount>0&&<div style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'#C08079',fontWeight:600}}>
              <AlertTriangle size={13}/>{overdueCount} overdue
            </div>}
            <div style={{width:1,height:24,background:'var(--border)'}}/>
          </>}
          {viewMode==='range'&&<>
            <span style={{fontSize:12,color:'var(--text3)',fontWeight:600}}>From</span>
            <input type="date" value={rangeStart} max={rangeEnd} onChange={e=>e.target.value&&setRangeStart(e.target.value)}
              style={{...inp,width:'auto',padding:'7px 10px',fontSize:12,background:'var(--bg3)'}}/>
            <span style={{fontSize:12,color:'var(--text3)',fontWeight:600}}>to</span>
            <input type="date" value={rangeEnd} min={rangeStart} onChange={e=>e.target.value&&setRangeEnd(e.target.value)}
              style={{...inp,width:'auto',padding:'7px 10px',fontSize:12,background:'var(--bg3)'}}/>
            <div style={{width:1,height:24,background:'var(--border)'}}/>
          </>}
          <select value={filterEmp} onChange={e=>setFilterEmp(e.target.value)}
            style={{...sel,width:'auto',padding:'7px 10px',fontSize:12,background:'var(--bg3)'}}>
            <option value="all">All employees</option>
            {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{...sel,width:'auto',padding:'7px 10px',fontSize:12,background:'var(--bg3)'}}>
            <option value="all">All statuses</option>
            {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </Card>

      {/* Task board — re-keyed per view so switching All / Day / Client review / Archive animates */}
      <div key={viewMode+'|'+(groupByClient?'g':'f')} className="page-anim">
      {visibleTasks.length===0
        ? <Card><div style={{padding:'3rem',textAlign:'center',color:'var(--text3)',fontSize:14}}>
            {viewMode==='day'?`No tasks for ${dayLabel}.`:viewMode==='range'?`No tasks between ${rangeLabel}.`:viewMode==='clientreview'?'Nothing awaiting client approval.':viewMode==='archive'?'Archive is empty.':'No tasks found.'}
            {editable&&viewMode!=='clientreview'&&viewMode!=='archive'&&<div style={{marginTop:12}}><Btn size="sm" variant="primary" onClick={openNew}><Plus size={12}/>Add first task</Btn></div>}
          </div></Card>
        : groupByClient && tasksByClient
          ? <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {tasksByClient.map(([client,tasks])=>(
                <ClientGroup key={client} clientName={client} tasks={tasks} {...cardProps}/>
              ))}
            </div>
          : <div className="stagger" style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,300px),1fr))',gap:18}}>
              {visibleTasks.map(task=><TaskCard key={task.id} task={task} {...cardProps}/>)}
            </div>
      }
      </div>

      {/* New / Edit modal */}
      <Modal open={showModal} onClose={()=>setShowModal(false)} title={editingTask?'Edit task':'New task'}>
        {editingTask && peers.length > 1 && <div style={{ marginBottom: 10 }}><PresenceBar users={peers} meId={myId} /></div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <Field label="Task name" style={{gridColumn:'1/-1'}}>
            <Input value={form.taskName} onChange={e=>setForm(p=>({...p,taskName:e.target.value}))}
              placeholder="e.g. Design carousel for Ramadan" autoFocus/>
          </Field>
          <Field label="Client / Brand name">
            <Input value={form.clientName} onChange={e=>setForm(p=>({...p,clientName:e.target.value}))}
              placeholder="e.g. Padel Edge"/>
          </Field>
          <Field label="Category">
            <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={sel}>
              {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Assign to (select multiple)" style={{gridColumn:'1/-1'}}>
            <div style={{display:'flex',flexDirection:'column',gap:4,background:'var(--bg3)',
              border:'1px solid var(--border2)',borderRadius:'var(--radius)',padding:'8px 10px',
              maxHeight:150,overflowY:'auto'}}>
              {[...employees].sort((a,b)=>(activeLoad[a.id]||0)-(activeLoad[b.id]||0)).map(e=>{
                const onLeave=isOnLeave(e.id,form.dueDate);
                const checked=(form.assignedToIds||[]).includes(e.id);
                return (
                  <label key={e.id} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'3px 0'}}>
                    <input type="checkbox" checked={checked}
                      onChange={()=>{
                        if(onLeave){showToast(`${e.name} is on approved leave.`,'error');return;}
                        const ids=form.assignedToIds||[];
                        setForm(p=>({...p,assignedToIds:checked?ids.filter(id=>id!==e.id):[...ids,e.id]}));
                      }}
                      style={{width:14,height:14,accentColor:'var(--accent)',flexShrink:0}}/>
                    <span style={{fontSize:13,color:onLeave?'var(--red)':checked?'var(--text)':'var(--text2)'}}>
                      {e.name}<span style={{fontSize:11,color:'var(--text3)',marginLeft:6}}>· {activeLoad[e.id]||0} active</span>{onLeave&&<span style={{fontSize:10,color:'var(--red)',marginLeft:5}}>(on leave)</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </Field>
          {(form.assignedToIds||[]).length >= 2 && (
            <Field label="Who needs to complete it?" style={{gridColumn:'1/-1'}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {[['any','Just one person'],['all','Everyone assigned'],['roles','By role']].map(([val,lbl])=>(
                  <button key={val} type="button" onClick={()=>setForm(p=>({...p,assignMode:val}))}
                    style={{padding:'7px 12px',borderRadius:'var(--radius)',fontSize:12.5,cursor:'pointer',fontWeight:600,
                      border:'1px solid '+((form.assignMode||'any')===val?'var(--accent)':'var(--border2)'),
                      background:(form.assignMode||'any')===val?'rgba(169,137,83,0.12)':'var(--bg3)',
                      color:(form.assignMode||'any')===val?'var(--accent)':'var(--text2)'}}>{lbl}</button>
                ))}
              </div>
              {form.assignMode==='roles' ? (()=>{
                const roles=[...new Set((form.assignedToIds||[]).flatMap(id=>{const e=employees.find(x=>x.id===id);return e?getEmpRoles(e):[];}))];
                const cr=form.completionRoles||{};
                const setN=(role,n)=>setForm(p=>({...p,completionRoles:{...(p.completionRoles||{}),[role]:Math.max(0,n)}}));
                const stepBtn={width:26,height:26,borderRadius:'var(--radius)',border:'1px solid var(--border2)',background:'var(--bg3)',cursor:'pointer',fontSize:15,lineHeight:1,color:'var(--text2)'};
                return (
                  <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:7}}>
                    {roles.map(role=>{
                      const n=cr[role]||0;
                      const avail=(form.assignedToIds||[]).filter(id=>{const e=employees.find(x=>x.id===id);return e&&getEmpRoles(e).includes(role);}).length;
                      return (
                        <div key={role} style={{display:'flex',alignItems:'center',gap:10}}>
                          <span style={{flex:1,fontSize:12.5,color:'var(--text)'}}>{role} <span style={{color:'var(--text3)'}}>· {avail} assigned</span></span>
                          <button type="button" onClick={()=>setN(role,n-1)} style={stepBtn}>−</button>
                          <span style={{minWidth:18,textAlign:'center',fontWeight:700,fontSize:13}}>{n}</span>
                          <button type="button" onClick={()=>setN(role,Math.min(avail,n+1))} style={stepBtn}>+</button>
                        </div>
                      );
                    })}
                    <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>Goes for review once this many people of each role have marked done. Set a role to 0 if it isn’t required.</div>
                  </div>
                );
              })() : (
                <div style={{fontSize:11,color:'var(--text3)',marginTop:5}}>
                  {(form.assignMode||'any')==='any' ? 'The first person to finish sends the whole task for review.' : 'Every assigned person must finish before it goes for review.'}
                </div>
              )}
            </Field>
          )}
          <Field label="Due date">
            <input type="date" value={form.dueDate}
              onChange={e=>setForm(p=>({...p,dueDate:e.target.value}))} style={inp}/>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={sel}>
              {STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Notes (optional)" style={{gridColumn:'1/-1'}}>
            <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))}
              rows={2} style={{...inp,resize:'vertical',fontFamily:'var(--font)'}}
              placeholder="Any additional details…"/>
          </Field>
          {/* Subtasks toggle */}
          <div style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:10,padding:'8px 12px',
            background:'var(--bg3)',borderRadius:'var(--radius)',border:'1px solid var(--border2)',cursor:'pointer'}}
            onClick={()=>setForm(p=>({...p,hasSubTasks:!p.hasSubTasks,
              subTasks:p.hasSubTasks?[]:(p.subTasks.length?p.subTasks:[{id:uniqueId(),title:'',done:false}])}))}>
            <input type="checkbox" checked={form.hasSubTasks} readOnly
              style={{width:15,height:15,accentColor:'var(--accent)',cursor:'pointer'}}/>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>This task has subtasks</div>
              <div style={{fontSize:11,color:'var(--text3)'}}>e.g. 3 Trendy Reels as individual items</div>
            </div>
          </div>
          {form.hasSubTasks&&(
            <div style={{gridColumn:'1/-1',display:'flex',flexDirection:'column',gap:8}}>
              <div style={{fontSize:10,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',
                textTransform:'uppercase',letterSpacing:'0.12em'}}>Subtasks</div>
              {form.subTasks.map((st,i)=>(
                <div key={st.id} style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:12,color:'var(--text3)',minWidth:16,textAlign:'right'}}>{i+1}.</span>
                  <Input value={st.title} onChange={e=>updateSubTask(st.id,e.target.value)}
                    placeholder={`Subtask ${i+1}…`} style={{flex:1}}/>
                  <button onClick={()=>removeSubTask(st.id)}
                    style={{background:'none',border:'none',cursor:'pointer',color:'var(--red)',fontSize:16,padding:'0 4px'}}>×</button>
                </div>
              ))}
              <Btn size="sm" variant="ghost" onClick={addSubTask}><Plus size={12}/>Add subtask</Btn>
            </div>
          )}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setShowModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveTask}>{editingTask?'Save changes':'Create task'}</Btn>
        </div>
      </Modal>

      {/* Final link prompt — shown when a designer/editor marks a task done */}
      <Modal open={!!completingId} onClose={()=>setCompletingId(null)} title="Submit your work for review">
        <Field label="Final Link / Where it was shared">
          <textarea value={linkText} onChange={e=>setLinkText(e.target.value)} rows={3}
            style={{...inp,resize:'vertical',fontFamily:'var(--mono)',fontSize:12.5,lineHeight:1.5}}
            placeholder="Paste the upload link, or note where you shared it — e.g. 'https://drive.google.com/…', 'Sent on Slack #design', 'Shared on WhatsApp group'"/>
        </Field>
        <p style={{fontSize:12,color:'var(--text3)',marginTop:-4}}>Required — tell your reviewer where to find the final deliverable.</p>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setCompletingId(null)}>Cancel</Btn>
          <Btn variant="primary" disabled={!linkText.trim()}
            onClick={()=>{ if(!linkText.trim()) return; markMyCompleted(completingId, linkText.trim()); setCompletingId(null); }}>
            Submit for review
          </Btn>
        </div>
      </Modal>

      {/* Reason prompt — shown when sending a task back for changes (internal or client) */}
      <Modal open={!!rejecting} onClose={()=>setRejecting(null)} title={rejecting?.by==='client'?'Client requested changes':'Request changes'}>
        <Field label="What needs to change?">
          <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3}
            style={{...inp,resize:'vertical',fontFamily:'var(--font)'}}
            placeholder={rejecting?.by==='client'?"e.g. Client wants the logo bigger and a different background colour":"e.g. Fix the spelling on slide 2 and tighten the intro"}/>
        </Field>
        <p style={{fontSize:12,color:'var(--text3)',marginTop:-4}}>This goes back to the assignee as a "Changes Required" note so they know exactly what to fix.</p>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1rem'}}>
          <Btn onClick={()=>setRejecting(null)}>Cancel</Btn>
          <Btn variant="primary" disabled={!rejectReason.trim()}
            onClick={()=>{ if(!rejectReason.trim()) return; updateStatus(rejecting.id,'Changes Required',rejectReason.trim(),rejecting.by); setRejecting(null); }}>
            Send back for changes
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
